/**
 * useLiveAssist — real-time in-call AI assist for the agent console.
 *
 * While a call is live it:
 *   1. opens a `/live-assist` session on the ws-gateway,
 *   2. streams the call's stereo PCM (agent mic + customer) to it,
 *   3. keeps the transcript (interim results replace in place by key),
 *   4. every `insightIntervalSec` with new speech, asks the API for insights
 *      (running summary, intent, sentiment, next-best actions, alerts, KB),
 * and when the call ends it closes the session and requests the post-call
 * summary. A socket drop mid-call starts a fresh session on reconnect and
 * keeps the transcript gathered so far.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { Socket } from 'socket.io-client'
import { api } from '../../api/client.js'
import { getSocket } from '../../socket/client.js'
import { useAuthStore } from '../../store/authStore.js'
import { callAudioTracks, startStereoCapture, trackKey, type StereoCapture } from './pcm-capture.js'

export interface LiveAssistConfig {
  enabled: boolean
  configured: boolean
  autoStart: boolean
  showKnowledge: boolean
  insightIntervalSec: number
  provider: string
  reason: string | null
}

export interface TranscriptSegment {
  key: string
  id?: string | null
  speaker: 'agent' | 'customer'
  text: string
  startMs: number
  endMs: number
  final: boolean
}

export interface Insight {
  id?: string
  summary: string
  customerIntent: string
  sentiment: 'positive' | 'neutral' | 'negative' | 'frustrated'
  nextBestActions: string[]
  suggestedResponse: string
  alerts: { type: string; message: string }[]
  knowledge: { id: string; title: string; snippet: string }[]
  createdAt?: string
  feedback?: 'up' | 'down' | null
}

export interface CallSummary {
  summary: string
  reason: string
  resolution: string
  outcome: 'resolved' | 'unresolved' | 'follow_up' | 'escalated'
  customerSentiment: string
  actionItems: string[]
  keyFacts: string[]
  followUp: string | null
  suggestedDisposition: string | null
}

export type AssistStatus = 'off' | 'idle' | 'connecting' | 'live' | 'reconnecting' | 'paused' | 'error' | 'ended'

export interface LiveAssistInput {
  peerConnection: RTCPeerConnection | null
  /** True while the call is connected (in call or on hold). */
  active: boolean
  callRef: string | null
  customerNumber: string | null
  direction: string | null
}

let nsp: Socket | null = null

/** The shared `/live-assist` namespace socket (multiplexed on the main connection). */
export function liveAssistSocket(): Socket {
  if (nsp) return nsp
  const manager = getSocket().io
  nsp = manager.socket('/live-assist', {
    auth: (cb: (d: { token?: string | undefined }) => void) => cb({ token: (useAuthStore.getState() as { accessToken?: string }).accessToken }),
  })
  return nsp
}

export function useLiveAssist(input: LiveAssistInput) {
  const [config, setConfig] = useState<LiveAssistConfig | null>(null)
  const [status, setStatus] = useState<AssistStatus>('off')
  const [error, setError] = useState<string | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [segments, setSegments] = useState<TranscriptSegment[]>([])
  const [insight, setInsight] = useState<Insight | null>(null)
  const [insightLoading, setInsightLoading] = useState(false)
  const [insightError, setInsightError] = useState<string | null>(null)
  const [summary, setSummary] = useState<CallSummary | null>(null)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [summaryError, setSummaryError] = useState<string | null>(null)
  const [levels, setLevels] = useState<{ agent: number; customer: number }>({ agent: 0, customer: 0 })
  const [manualOn, setManualOn] = useState(false)
  const [paused, setPaused] = useState(false)

  const captureRef = useRef<StereoCapture | null>(null)
  const sessionRef = useRef<string | null>(null)
  const sessionsRef = useRef<string[]>([])
  const finalsSinceInsight = useRef(0)
  const insightBusy = useRef(false)
  const lastLevels = useRef(0)
  const lastMicSound = useRef(0)
  const pausedRef = useRef(false)
  pausedRef.current = paused

  useEffect(() => {
    api.get<LiveAssistConfig>('/live-assist/config').then(setConfig).catch(() => setConfig(null))
  }, [])

  const wanted = Boolean(config?.enabled && input.active && input.peerConnection && (config.autoStart || manualOn))

  // ── Session + capture lifecycle ─────────────────────────────────────────
  useEffect(() => {
    if (!wanted || !input.peerConnection) return
    const socket = liveAssistSocket()
    const pc = input.peerConnection
    let disposed = false

    const onSegment = (seg: TranscriptSegment & { sessionId: string }): void => {
      if (!sessionsRef.current.includes(seg.sessionId)) return
      const key = `${seg.sessionId}:${seg.key}`
      setSegments((prev) => {
        const i = prev.findIndex((s) => s.key === key)
        const next = { ...seg, key }
        if (i >= 0) {
          const copy = prev.slice()
          copy[i] = next
          return copy
        }
        return [...prev, next].slice(-600)
      })
      if (seg.final) finalsSinceInsight.current++
    }
    const onStatus = (s: { sessionId: string; state: string; message?: string }): void => {
      if (s.sessionId !== sessionRef.current) return
      if (s.state === 'error') {
        setStatus('error')
        setError(s.message ?? 'Transcription stopped.')
      }
    }

    // ── Capture binding ──────────────────────────────────────────────────
    // Exactly one capture feeds a session: every (re)bind bumps `bindSeq`,
    // and a bind that finishes after a newer one started throws itself away.
    // Without this, two captures could stream into one session at once —
    // AWS then receives interleaved audio and stops recognising anything.
    let bindSeq = 0
    let rebinding: Promise<void> | null = null
    const bindCapture = async (): Promise<void> => {
      const seq = ++bindSeq
      captureRef.current?.stop()
      captureRef.current = null
      const cap = await startStereoCapture(pc, {
        // Paused → send silence of the same length instead of nothing: the
        // STT providers close an idle stream (AWS after 15 s), and silence
        // carries no customer audio.
        onChunk: (pcm) => { if (socket.connected) socket.emit('assist:audio', pausedRef.current ? new ArrayBuffer(pcm.byteLength) : pcm) },
        onLevels: (a, c) => {
          const now = performance.now()
          if (a > 0) lastMicSound.current = now
          if (now - lastLevels.current > 150) {
            lastLevels.current = now
            setLevels({ agent: Math.min(1, a * 4), customer: Math.min(1, c * 4) })
          }
        },
      })
      if (disposed || seq !== bindSeq) { cap.stop(); return }
      captureRef.current = cap
      lastMicSound.current = performance.now()
    }
    /** Re-read the call's tracks; rebuild the capture if they changed (or `force`). */
    const rebindIfNeeded = (force = false): void => {
      if (disposed || !sessionRef.current || rebinding) return
      if (!force && captureRef.current && captureRef.current.key === trackKey(pc)) return
      rebinding = bindCapture().catch(() => undefined).finally(() => { rebinding = null })
    }
    // A re-INVITE (hold/unhold, Asterisk bridge change, codec change) can swap
    // the call's tracks underneath us — the old ones then deliver silence.
    const onSignaling = (): void => { if (pc.signalingState === 'stable') rebindIfNeeded() }
    const onTrack = (): void => { rebindIfNeeded() }
    pc.addEventListener('signalingstatechange', onSignaling)
    pc.addEventListener('track', onTrack)
    // Watchdog: a live, unmuted microphone is never digitally silent. If it
    // has been exactly silent for 5 s, the capture is reading a dead track.
    let lastForced = 0
    const watchdog = setInterval(() => {
      if (!captureRef.current || pausedRef.current) return
      const { mic } = callAudioTracks(pc)
      const now = performance.now()
      // Back off 15 s between forced rebuilds so a genuinely dead mic doesn't churn.
      if (mic && mic.enabled && !mic.muted && now - lastMicSound.current > 5_000 && now - lastForced > 15_000) {
        lastForced = now
        rebindIfNeeded(true)
      } else rebindIfNeeded()
    }, 2_000)

    // One start at a time: 'connect' can fire while the initial start is
    // still awaiting its ack, which used to open two server sessions.
    let beginning = false
    const begin = async (): Promise<void> => {
      if (beginning) return
      beginning = true
      try { await beginOnce() } finally { beginning = false }
    }
    const beginOnce = async (): Promise<void> => {
      setStatus((cur) => (cur === 'reconnecting' ? cur : 'connecting'))
      setError(null)
      try {
        const ack = await socket.timeout(15_000).emitWithAck('assist:start', {
          callRef: input.callRef, customerNumber: input.customerNumber,
          direction: input.direction?.toLowerCase() === 'outbound' ? 'outbound' : input.direction ? 'inbound' : null,
        }) as { ok: boolean; sessionId?: string; message?: string }
        if (disposed) return
        if (!ack.ok || !ack.sessionId) {
          setStatus('error')
          setError(ack.message ?? 'Live assist could not start.')
          return
        }
        sessionRef.current = ack.sessionId
        sessionsRef.current = [...sessionsRef.current, ack.sessionId]
        setSessionId(ack.sessionId)
        await bindCapture()
        if (disposed) return
        setStatus(pausedRef.current ? 'paused' : 'live')
      } catch (err) {
        if (disposed) return
        setStatus('error')
        setError(err instanceof Error && err.message !== 'operation has timed out' ? err.message : 'The live assist service did not respond.')
      }
    }

    const onConnect = (): void => { void begin() }
    const onDisconnect = (): void => {
      captureRef.current?.stop()
      captureRef.current = null
      if (!disposed) setStatus('reconnecting')
    }

    socket.on('assist:segment', onSegment)
    socket.on('assist:status', onStatus)
    socket.on('connect', onConnect)
    socket.on('disconnect', onDisconnect)
    if (socket.connected) void begin()
    else socket.connect()

    return () => {
      disposed = true
      clearInterval(watchdog)
      pc.removeEventListener('signalingstatechange', onSignaling)
      pc.removeEventListener('track', onTrack)
      socket.off('assist:segment', onSegment)
      socket.off('assist:status', onStatus)
      socket.off('connect', onConnect)
      socket.off('disconnect', onDisconnect)
      captureRef.current?.stop()
      captureRef.current = null
      if (socket.connected) socket.emit('assist:stop', {})
    }
    // Restart only when the call (peer) changes, not on every caller-id refresh.
  }, [wanted, input.peerConnection])

  // ── New call: reset everything ──────────────────────────────────────────
  const prevActive = useRef(false)
  useEffect(() => {
    if (input.active && !prevActive.current) {
      setSegments([])
      setInsight(null)
      setInsightError(null)
      setSummary(null)
      setSummaryError(null)
      setSessionId(null)
      setPaused(false)
      setManualOn(false)
      sessionRef.current = null
      sessionsRef.current = []
      finalsSinceInsight.current = 0
      setStatus(config?.enabled ? 'idle' : 'off')
    }
    prevActive.current = input.active
  }, [input.active, config?.enabled])

  // ── Insights cadence ────────────────────────────────────────────────────
  const requestInsight = useCallback(async (force = false): Promise<void> => {
    const sid = sessionRef.current
    if (!sid || insightBusy.current) return
    if (!force && finalsSinceInsight.current === 0) return
    insightBusy.current = true
    setInsightLoading(true)
    const pending = finalsSinceInsight.current
    try {
      const r = await api.post<{ insight: Insight | null }>(`/live-assist/sessions/${sid}/insights`)
      if (r.insight) setInsight(r.insight)
      finalsSinceInsight.current = Math.max(0, finalsSinceInsight.current - pending)
      setInsightError(null)
    } catch (err) {
      setInsightError((err as { body?: { message?: string } })?.body?.message ?? 'Insights unavailable right now.')
    } finally {
      insightBusy.current = false
      setInsightLoading(false)
    }
  }, [])

  useEffect(() => {
    if (status !== 'live' || !config) return
    const t = setInterval(() => { void requestInsight() }, Math.max(5, config.insightIntervalSec) * 1000)
    return () => clearInterval(t)
  }, [status, config, requestInsight])

  // ── Call ended → post-call summary ──────────────────────────────────────
  const requestSummary = useCallback(async (force = false): Promise<void> => {
    const sid = sessionsRef.current[sessionsRef.current.length - 1]
    if (!sid) return
    setSummaryLoading(true)
    setSummaryError(null)
    try {
      // A mid-call reconnect splits the transcript across sessions; the
      // latest session is summarised (it holds the end of the call).
      const r = await api.post<{ summary: CallSummary }>(`/live-assist/sessions/${sid}/summary${force ? '?force=true' : ''}`)
      setSummary(r.summary)
    } catch (err) {
      setSummaryError((err as { body?: { message?: string } })?.body?.message ?? 'Could not create the call summary.')
    } finally {
      setSummaryLoading(false)
    }
  }, [])

  useEffect(() => {
    if (input.active || sessionsRef.current.length === 0) return
    setStatus('ended')
    // Let the gateway flush the last final segments before summarising.
    const t = setTimeout(() => { void requestSummary() }, 2500)
    return () => clearTimeout(t)
  }, [input.active, requestSummary])

  const sendFeedback = useCallback(async (value: 'up' | 'down'): Promise<void> => {
    if (!insight?.id) return
    const next = insight.feedback === value ? null : value
    setInsight({ ...insight, feedback: next })
    await api.post(`/live-assist/insights/${insight.id}/feedback`, { feedback: next }).catch(() => undefined)
  }, [insight])

  const togglePause = useCallback(() => {
    setPaused((p) => {
      const next = !p
      setStatus((s) => (s === 'live' || s === 'paused' ? (next ? 'paused' : 'live') : s))
      return next
    })
  }, [])

  const dismiss = useCallback(() => {
    setStatus('off')
    setSummary(null)
    setSegments([])
    setInsight(null)
    sessionsRef.current = []
  }, [])

  return {
    config,
    status,
    error,
    sessionId,
    segments,
    insight,
    insightLoading,
    insightError,
    summary,
    summaryLoading,
    summaryError,
    levels,
    paused,
    start: () => setManualOn(true),
    togglePause,
    requestInsight: () => requestInsight(true),
    requestSummary: () => requestSummary(true),
    sendFeedback,
    dismiss,
  }
}

export type LiveAssistState = ReturnType<typeof useLiveAssist>
